import React, { useEffect, useRef, useState } from 'react';
import { Image } from '@core/js/components/common/Image';
import { compose, omit, path, pick, prop, identity } from 'ramda';
import { useGameConfigurationSelector, useAppSelector, useDeviceSelector } from '@core/js/store/hooks';
import { useLiveGameConfig, useGameImageUrls } from '@core/js/hooks/games';
import { withTranslations } from '@core/js/components/hocs/withTranslations';
import { AccessibilityElement } from '@core/js/components/common/AccessibilityElement';
import { branch, mapProps, pure, renderNothing, withHandlers, withProps } from 'recompose';

import { FavoriteIcon } from '@theme/js/components/common/Game/FavoriteIcon/FavoriteIcon';
import withGameLaunch from '@core/js/components/common/Game/withGameLaunch';
import { withGameInfo } from '@core/js/components/common/Game/withGameInfo';
import { Loader } from '@core/js/components/common/Loader';
import { useNavigate } from '@theme/js/hooks/navigate';
import { useAssetRedirectData } from '@theme/js/components/common/Game/hooks/useAssetRedirectData';
import { PlayButton } from '@theme/js/components/main/Games/Buttons';

import style from './Game.scss';

export const Description = withTranslations(({ description, classNames }) => {
    const MAX_LENGTH = 145;
    const value = description?.length > MAX_LENGTH ? `${description.substring(0, MAX_LENGTH)}...` : description;

    return value && <p className={classNames?.gameDescription}>{value}</p>;
});

export const useHideHoverState = (ref, hideHoverState) => {
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                hideHoverState();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref, hideHoverState]);
};

export const GameBare = ({
    t,
    code,
    name,
    size = 'extraSmall',
    showTitle = false,
    showFeatureIcons = true,
    gameFeed = '', /* Lugano changes */
    showFavoriteIcon,
    showRecentlyPlayedIcon,
    className = '',
    setGameRef,
    setImageRef,
    setGameImageRef,
    onClick,
    onMouseMove,
    isGameNew,
    isGameHot,
    isGamePopular,
    isGameExclusive,
    tabIndex,
    liveLaunchAlias,
    includeLiveGames = false,
    displayMode = '',
    classNames = {}, /* Lugano changes */
    isDesktop, /* Lugano changes */
    isMobile, /* Lugano changes */
    isBonusWithGames = false, /* Lugano changes */
    isHideDynamicImages, /* Lugano changes */
    bonusPopupId, /* Lugano changes */
    onHoverChange, /* Lugano changes */
    gameGridType /* Lugano changes */
}) => {
    const [isImageLoaded, setIsImageLoaded] = useState(false); /* Lugano changes */
    const liveGameConfig = useLiveGameConfig(liveLaunchAlias);
    const isLiveGame = includeLiveGames && liveGameConfig?.launchAlias;
    const { dynamicImageUrl, limits, isScheduled, openingDateTime, dealerName, language, seatsInfo } = liveGameConfig || {};
    const isScheduledLiveGame = isLiveGame && isScheduled;

    /* Lugano changes */
    const imageUrls = useGameImageUrls({
        code,
        dynamicImageUrl,
        isLiveGame: isLiveGame && !isHideDynamicImages
    });
    const hoverRef = useRef(null);
    const [isHover, setIsHover] = useState(false);
    const { description } = useGameConfigurationSelector(path(['games', code]));
    const dynamicTags = useAppSelector(prop('dynamicTags'));
    const { isArticleIdPathPresent, linkToPage } = useAssetRedirectData(code, gameGridType);

    const hotLabelIcon = dynamicTags.DTAG_GAME_LABEL_ICON_FONT_NAME_HOT || 'icon-ico-s4w-stars';
    const newLabelIcon = dynamicTags.DTAG_GAME_LABEL_ICON_FONT_NAME_NEW || 'icon-ico-s4w-stars';
    const exclusiveLabelIcon = dynamicTags.DTAG_GAME_LABEL_ICON_FONT_NAME_EXCLUSIVE || 'icon-ico-s4w-exclusive';

    const showHoverState = () => {
        setIsHover(true);
        onHoverChange && onHoverChange(true);
    };
    const hideHoverState = () => {
        setIsHover(false);
        onHoverChange && onHoverChange(false);
    };

    const imageLoaded = () => setIsImageLoaded(true); /* Lugano changes */

    if (!isDesktop) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useHideHoverState(hoverRef, hideHoverState);
    }

    const navigate = useNavigate();
    const navigateToCustomGameInfo = () => {
        navigate(linkToPage);
        if (isBonusWithGames) {
            window.Playtech.API.popup.closePopups(bonusPopupId);
        }
    };

    const launchGameWithAsset = isArticleIdPathPresent && (isHover && isMobile || isBonusWithGames)
        ? () => navigateToCustomGameInfo()
        : isMobile && isHover || isBonusWithGames ? onClick : null;

    return (
        <AccessibilityElement
            onClick={launchGameWithAsset}
            tabIndex={tabIndex}
            data-game-code={code}
            elementRef={setGameRef}
            className={`${style.container} ${style[size]} ${style[displayMode] || ''} ${className}`}
            aria-label={`${t('playtech.mobile.system.game.aria-label')} ${name}`}
        >
            <div ref={hoverRef}
                 onClick={isDesktop ? null : showHoverState}
                 onMouseEnter={isDesktop ? showHoverState : null}
                 onMouseLeave={isDesktop ? hideHoverState : null}
                 className={`${style.topSection} ${isHover ? style.hoverContainer : ''}`}
            >
                {isHover && !isBonusWithGames && (
                    <div className={style.hoverWrapper}>
                        <div className={style.buttonContainer}>
                            <PlayButton gameFeed={gameFeed} linkToPage={linkToPage} code={code} isHoverBtn={true} className={style.playButton} tKey="" />
                        </div>
                        <div className={style.gameInfo}>
                            <h3 className={style.gameName}>{name}</h3>
                            <Description
                                classNames={{
                                    gameDescription: style.gameDescription
                                }}
                                description={description}
                            />
                        </div>
                    </div>
                )}
                <div
                    aria-hidden={true}
                    ref={setGameImageRef}
                    onMouseMove={onMouseMove}
                    className={style.imageContainer}
                >
                    <Image
                        alt={name}
                        src={imageUrls}
                        ref={setImageRef}
                        className={`${style.image} ${classNames?.image || ''}`}
                        onLoad={imageLoaded} /* Lugano changes */
                    />
                    {!isImageLoaded && <Loader className={style.loader} />} {/* Lugano changes */}
                    {isScheduledLiveGame && <div className={style.scheduledLiveGameBackground} />}
                    <div className={`${style.background} ${classNames?.background || ''}`} />
                    {showRecentlyPlayedIcon
                        && <div className={`${style.recentlyPlayedIcon} ${classNames?.gameRecentlyPlayedIcon}`} />
                    }
                </div>

                <div className={`${style.sideLabels} ${showTitle ? style.withTitle : ''}`} aria-hidden={true}>
                    {showFeatureIcons && !isHover && (
                        <div className={style.labels}>
                            {isGameNew &&
                                <span className={style.label}>
                                    <span className={newLabelIcon} />
                                    <span>{t('playtech.mobile.responsivegames.game.label.new')}</span>
                                </span>
                            }
                            {(!isGameNew || !isGameExclusive) && isGameHot &&
                                <span className={style.label}><span className={hotLabelIcon} />
                                    <span>{t('playtech.mobile.responsivegames.game.label.hot')}</span>
                                </span>
                            }
                            {isGameExclusive &&
                                <span className={style.label}>
                                    <span className={exclusiveLabelIcon} />
                                    <span>{t('playtech.mobile.responsivegames.game.label.exclusive')}</span>
                                </span>
                            }
                            {isGamePopular &&
                                <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.popular')}</span>
                            }
                        </div>
                    )}
                    {isLiveGame && limits && !isHover && <div className={style.liveLimits}>{limits}</div>}
                </div>
                {isScheduledLiveGame && (
                    <div className={`${style.scheduledLiveGameInfo} ${showTitle ? style.withTitle : ''}`} aria-hidden={true}>
                        {openingDateTime
                            ? (
                                <>
                                    <span>{t('playtech.mobile.system.liveGame.startsAt')}</span>
                                    <span>{openingDateTime}</span>
                                </>
                            )
                            : <span>{t('playtech.mobile.system.liveGame.startsSoon')}</span>
                        }
                    </div>
                )}

                <div className={`${style.sideIcons} ${showTitle ? style.withTitle : ''} ${isLiveGame ? style.sideIconsLive : ''}`}>
                    {isLiveGame && !isHover && (
                        <div className={`${style.liveInfo} ${style.liveInfo}`} aria-hidden={true}>
                            <span>{dealerName}</span>
                            <span>{language}</span>
                        </div>
                    )}
                    {showFavoriteIcon && (
                        <FavoriteIcon
                            code={code}
                            gameFeed={gameFeed}
                            className={style.favorite}
                        />
                    )}
                    {isLiveGame && seatsInfo && !isHover && (
                        <div className={style.seatsInfo} aria-hidden={true}>
                            <span>{seatsInfo}</span>
                            <span className={style.seatsIcon} />
                        </div>
                    )}
                </div>
                {showTitle && (
                    <div className={style.titleWrapper}>
                        <span className={`${style.name} ${style.ellipsis}`} aria-hidden={true}>{name}</span>
                    </div>
                )}
            </div>
        </AccessibilityElement>
    );
};

export const isGameAvailable = compose(
    withProps(({ code }) => ({ isAvailable: Boolean(useGameConfigurationSelector(path(['games', code]))) })),
    branch(
        props => !props.isAvailable,
        renderNothing
    ),
    mapProps(omit(['isAvailable']))
);

export const enhance = compose(
    pure,
    withTranslations,
    isGameAvailable,
    branch(
        ({ showGameInfo }) => showGameInfo,
        withGameInfo,
        withGameLaunch
    ),
    /* Lugano changes */
    withHandlers(({ launchGame, openGameInfo, disableGameLaunch, openOnGameClick }) => ({
        onClick: () => (event) => {
            if (openOnGameClick) {
                const onGameClick = disableGameLaunch ? identity : launchGame || openGameInfo;
                event.preventDefault();
                onGameClick();
            }
        }
    })),
    withProps(() => useDeviceSelector(pick(['isDesktop', 'isTablet', 'isMobile']))),
    /* Lugano changes */
    withProps(({ code }) => ({
        ...useGameConfigurationSelector(path(['games', code]))
    })),
    mapProps(omit(['description', 'demoModeSupport', 'jackpotCode']))
);

export const Game = enhance(GameBare);
